import { getProductDetailQuoteListing } from "@iewc/Services/CommonService";
import { colorStrings } from "@iewc/Utilities/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: any) => {
    const page = getCurrentPage(state);
    const pageType = page?.type;
    return {
        pageType,
        isAuthenticated: state.context?.session?.isAuthenticated && !state.context?.session?.isGuest,
    };
};

export interface ProductDetailsQuoteListingStyles {
    wrapper?: InjectableCss;
    heading?: TypographyPresentationProps;
}

export const productDetailsQuoteListingStyles: ProductDetailsQuoteListingStyles = {
    wrapper: {
        css: css`
            margin-block: ;
            padding: 16px;
            background-color: ${getColor(colorStrings.dangerContrast)};
            border: 1px solid ${getColor(colorStrings.dangerMain)};

            .pdp-quote__listing {
                display: flex;
                flex-direction: column;
                list-style-type: none;
                padding: 0;
                margin: 0;
                gap: 12px;
                color: ${getColor(colorStrings.dangerMain)};
            }
        `,
    },
    heading: {
        variant: "h6",
        color: colorStrings.dangerMain,
        css: css`
            margin-block-end: 8px;
        `,
    },
};

type OwnProps = {
    productID: string;
};

type PDPQuoteListing = {
    Properties: {};
    Uri: string | null;
    erpOrderNumber: string | null;
    quoteUrl: string | null;
};

type Props = OwnProps & WidgetProps & HasShellContext & HasProduct & ReturnType<typeof mapStateToProps>;

const ProductDetailsQuoteListing = ({ isAuthenticated, productInfo, shellContext, pageType }: Props) => {
    const productId = productInfo.pricing?.additionalResults?.erpProductNumber;
    const styles = productDetailsQuoteListingStyles;
    const [quoteList, setQuoteList] = React.useState<any[]>([]);
    const { isInShell } = shellContext;
    const isPageDetail = pageType === "ProductDetailsPage";

    if (isInShell && isPageDetail) {
        return (
            <StyledWrapper
                css={css`
                    display: flex;
                    justify-content: center;
                `}
            >
                <Typography
                    css={css`
                        font-size: 16px;
                        color: #000;
                    `}
                >
                    Product Details Quote Listing Edit Mode
                </Typography>
            </StyledWrapper>
        );
    }

    const fetchPDPQuoteListing = async () => {
        const data = (await getProductDetailQuoteListing({
            id: productId as string,
        })) as any;

        if (data.successful) {
            const result = data.result as PDPQuoteListing[];

            const items = result.map(item => {
                return {
                    text: item.erpOrderNumber,
                    link: item.quoteUrl,
                };
            });

            // Ensure a new array reference is created
            setQuoteList(items);
        }
    };

    useEffect(() => {
        if (productId) {
            fetchPDPQuoteListing();
        }
    }, [productId]);

    if (!isAuthenticated || (quoteList && quoteList.length === 0)) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.heading}>{translate("ProductDetailQuoteListingHeading")}</Typography>
            <ul className="pdp-quote__listing">
                {quoteList.map((item, index) => (
                    <li key={index}>
                        <Link href={item.link} target="_blank">
                            {item.text}
                        </Link>
                    </li>
                ))}
            </ul>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(withIsInShell(ProductDetailsQuoteListing as any) as any) as any),
    definition: {
        group: "Product Details",
        displayName: "Product Details Quote Listing",
        allowedContexts: ["ProductDetailsPage"],
    },
};
export default widgetModule;
